import { useState } from 'react'
import RuleBuilder from './RuleBuilder.jsx'

// Édition d'une question. Le nom technique est relégué en bas, replié :
// il ne sert qu'aux courriels, personne ne devrait avoir à y penser.
export default function QuestionForm({ question, catalogue, vocabulaire,
                                       onEnregistrer, onSupprimer, onAnnuler }) {
  const [q, setQ] = useState({ ...question })
  const [technique, setTechnique] = useState(false)
  const [formats, setFormats] = useState([])

  useEffect(() => {
    api.formats(q.type).then(setFormats).catch(() => setFormats([]))
  }, [q.type])
  const maj = (champs) => setQ({ ...q, ...champs })

  const saisie = catalogue.input_types.includes(q.type)
  const avecOptions = catalogue.with_options.includes(q.type)
  const titreType = catalogue.families
    .flatMap((f) => f.items)
    .find((i) => i.type === q.type)?.titre || q.type

  // Le nom technique suit l'intitulé tant que personne ne l'a fixé à la main.
  function changerLibelle(label) {
    if (!saisie) return maj({ label })
    const auto = !q.varname || q.varname === slugifier(q.label)
    maj(auto ? { label, varname: slugifier(label) } : { label })
  }

  return (
    <div className="fenetre" role="dialog" aria-label="Réglages de la question">
      <div className="fenetre-corps large">
        <h2>{titreType}</h2>

        <div className="champ">
          <label htmlFor="q-label">
            {q.type === 'comment' ? 'Titre (facultatif)' : "Ce que lit l'habitant"}
          </label>
          <input id="q-label" value={q.label || ''} autoFocus
                 onChange={(e) => changerLibelle(e.target.value)} />
        </div>

        {q.type === 'comment' && (
          <div className="champ">
            <label htmlFor="q-texte">Texte affiché</label>
            <textarea id="q-texte" rows={3} value={q.hint || ''}
                      onChange={(e) => maj({ hint: e.target.value })} />
          </div>
        )}

        {saisie && (
          <>
            <div className="champ">
              <label htmlFor="q-req">Faut-il une réponse ?</label>
              <select id="q-req" value={q.required ? '1' : '0'}
                      onChange={(e) => maj({ required: e.target.value === '1' })}>
                <option value="0">Facultative</option>
                <option value="1">Obligatoire, on ne peut pas continuer sans</option>
              </select>
            </div>

            {formats.length > 0 && (
              <div className="champ">
                <label htmlFor="q-format">Format attendu</label>
                <select id="q-format" value={q.format || ''}
                        onChange={(e) => maj({ format: e.target.value || undefined })}>
                  <option value="">Aucun contrôle particulier</option>
                  {formats.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  <option value="regex">Expression personnalisée…</option>
                </select>
                {q.format && q.format !== 'regex' && (
                  <p className="aide">
                    {formats.find((f) => f.key === q.format)?.aide}
                    {' '}Exemple montré à l'habitant :
                    {' '}<code>{formats.find((f) => f.key === q.format)?.exemple}</code>
                  </p>
                )}
                {!q.format && (
                  <p className="aide">Une réponse mal recopiée se découvre un mois plus tard, au
                    téléphone. Un format évite l'aller-retour.</p>
                )}
              </div>
            )}

            {q.format === 'regex' && (
              <>
                <div className="champ">
                  <label htmlFor="q-pattern">Expression personnalisée</label>
                  <input id="q-pattern" className="mono" value={q.pattern || ''}
                         onChange={(e) => maj({ pattern: e.target.value })} />
                  <p className="aide">Pour les cas que la liste ne couvre pas. Si vous n'êtes pas
                    sûr, revenez à un format de la liste.</p>
                </div>
                <div className="champ">
                  <label htmlFor="q-pmsg">Message affiché si la réponse ne convient pas</label>
                  <input id="q-pmsg" value={q.pattern_message || ''}
                         placeholder="Cette réponse n'a pas le format attendu."
                         onChange={(e) => maj({ pattern_message: e.target.value })} />
                </div>
              </>
            )}

            <div className="champ">
              <label htmlFor="q-hint">Petite phrase d'aide</label>
              <input id="q-hint" value={q.hint || ''} placeholder="Affichée en gris sous la question"
                     onChange={(e) => maj({ hint: e.target.value })} />
            </div>
          </>
        )}

        {avecOptions && (
          <div className="champ">
            <label htmlFor="q-options">Réponses proposées</label>
            <textarea id="q-options" rows={5} value={(q.options || []).join('\n')}
                      placeholder="Une réponse par ligne"
                      onChange={(e) => maj({
                        options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean),
                      })} />
            <p className="aide">Une par ligne. C'est aussi cette liste qui sert aux règles
              d'affichage des questions suivantes.</p>
          </div>
        )}

        {saisie && (
          <>
            <hr />
            <RuleBuilder vocabulaire={vocabulaire} valeur={q.condition}
                         onChange={(condition) => maj({ condition })} />

            <button className="replier" aria-expanded={technique}
                    onClick={() => setTechnique(!technique)}>
              {technique ? '▾' : '▸'} Nom technique
            </button>
            {technique && (
              <div className="champ">
                <input className="mono" value={q.varname || ''}
                       onChange={(e) => maj({ varname: slugifier(e.target.value) })} />
                <p className="aide">Sert à reprendre la réponse dans les courriels, sous la forme
                  {' '}<code>{`{{ form_var_${q.varname || 'nom'} }}`}</code>. Inutile d'y toucher
                  si vous ne vous en servez pas.</p>
              </div>
            )}
          </>
        )}

        <div className="ligne separee">
          {onSupprimer && (
            <button className="btn dgr" onClick={() => onSupprimer(q)}>Supprimer la question</button>
          )}
          <div className="pousse" />
          <button className="btn" onClick={onAnnuler}>Annuler</button>
          <button className="btn pri" onClick={() => onEnregistrer(q)}>Enregistrer</button>
        </div>
      </div>
    </div>
  )
}

export function slugifier(texte) {
  return (texte || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
}
